import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import { collection, query, where, getDocs, addDoc, orderBy, Timestamp } from 'firebase/firestore';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Plus, Search, TrendingUp, Building2, DollarSign, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const dealSchema = z.object({
  businessName: z.string().min(2, 'Business name must be at least 2 characters').max(100),
  industry: z.string().min(2, 'Industry is required'),
  sector: z.string().min(2, 'Sector is required'),
  dealValue: z.string().min(1, 'Deal value is required'),
  status: z.enum(['lead', 'qualification', 'proposal', 'negotiation', 'closing', 'won', 'lost']),
  description: z.string().min(10, 'Description must be at least 10 characters').max(500),
  contactPerson: z.string().min(2, 'Contact person is required').max(100),
  contactEmail: z.string().email('Invalid email address'),
  expectedCloseDate: z.string().min(1, 'Expected close date is required'),
});

type DealFormData = z.infer<typeof dealSchema>;

interface Deal {
  id: string;
  businessName: string;
  industry: string;
  sector: string;
  dealValue: number;
  status: string;
  description: string;
  contactPerson: string;
  contactEmail: string;
  expectedCloseDate: Date;
  createdAt: Date;
  updatedAt: Date;
  userId: string;
}

export default function BrokerListings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const form = useForm<DealFormData>({
    resolver: zodResolver(dealSchema),
    defaultValues: {
      businessName: '',
      industry: '',
      sector: '',
      dealValue: '',
      status: 'lead',
      description: '',
      contactPerson: '',
      contactEmail: '',
      expectedCloseDate: '',
    },
  });

  useEffect(() => {
    fetchDeals();
  }, [user]);

  const fetchDeals = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const dealsQuery = query(
        collection(db, 'dealSubmissions'),
        where('submittedBy', '==', user.id),
        orderBy('createdAt', 'desc')
      );
      const dealsSnapshot = await getDocs(dealsQuery);
      const dealsList = dealsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        expectedCloseDate: doc.data().expectedCloseDate?.toDate(),
      })) as Deal[];
      setDeals(dealsList);
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: DealFormData) => {
    if (!user) return;
    try {
      const dealData = {
        ...data,
        dealValue: parseFloat(data.dealValue),
        expectedCloseDate: Timestamp.fromDate(new Date(data.expectedCloseDate)),
        userId: user.id,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      await addDoc(collection(db, 'deals'), dealData);
      toast({ title: 'Success', description: 'Deal created successfully' });
      form.reset();
      setIsDialogOpen(false);
      fetchDeals();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create deal', variant: 'destructive' });
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      lead: 'bg-blue-500', qualification: 'bg-purple-500', proposal: 'bg-yellow-500',
      negotiation: 'bg-orange-500', closing: 'bg-green-500', won: 'bg-emerald-600', lost: 'bg-red-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  const filteredDeals = deals.filter(deal => {
    const matchesSearch = deal.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         deal.industry.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || deal.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const kpis = {
    totalValue: deals.reduce((sum, d) => sum + d.dealValue, 0),
    wonValue: deals.filter(d => d.status === 'won').reduce((sum, d) => sum + d.dealValue, 0),
    wonDeals: deals.filter(d => d.status === 'won').length,
    activeDeals: deals.filter(d => !['won', 'lost'].includes(d.status)).length,
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">My Listings</h1>
          <p className="text-muted-foreground mt-1">Track KPIs and deal stages (seller: listed business / buyer: contacted businesses)</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" />Add Deal</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Deal</DialogTitle>
              <DialogDescription>Add a new deal to your pipeline</DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="businessName" render={({ field }) => (
                    <FormItem><FormLabel>Business Name</FormLabel><FormControl><Input placeholder="Enter business name" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="industry" render={({ field }) => (
                    <FormItem><FormLabel>Industry</FormLabel><FormControl><Input placeholder="e.g., Technology" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="sector" render={({ field }) => (
                    <FormItem><FormLabel>Sector</FormLabel><FormControl><Input placeholder="e.g., SaaS" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="dealValue" render={({ field }) => (
                    <FormItem><FormLabel>Deal Value ($)</FormLabel><FormControl><Input type="number" placeholder="0" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="status" render={({ field }) => (
                    <FormItem><FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="lead">Lead</SelectItem><SelectItem value="qualification">Qualification</SelectItem>
                          <SelectItem value="proposal">Proposal</SelectItem><SelectItem value="negotiation">Negotiation</SelectItem>
                          <SelectItem value="closing">Closing</SelectItem><SelectItem value="won">Won</SelectItem><SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select><FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="expectedCloseDate" render={({ field }) => (
                    <FormItem><FormLabel>Expected Close Date</FormLabel><FormControl><Input type="date" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem><FormLabel>Description</FormLabel><FormControl><Textarea placeholder="Describe the deal opportunity..." className="min-h-[80px]" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="contactPerson" render={({ field }) => (
                    <FormItem><FormLabel>Contact Person</FormLabel><FormControl><Input placeholder="John Doe" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="contactEmail" render={({ field }) => (
                    <FormItem><FormLabel>Contact Email</FormLabel><FormControl><Input type="email" placeholder="john@example.com" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Create Deal</Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Total Pipeline Value</CardTitle><DollarSign className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">${kpis.totalValue.toLocaleString()}</div></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Active Deals</CardTitle><TrendingUp className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{kpis.activeDeals}</div></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Won Deals</CardTitle><Building2 className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{kpis.wonDeals}</div></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Won Value</CardTitle><DollarSign className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">${kpis.wonValue.toLocaleString()}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Deal Pipeline</CardTitle><CardDescription>Manage and track all your deals</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by business name or industry..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter by status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem><SelectItem value="lead">Lead</SelectItem><SelectItem value="qualification">Qualification</SelectItem>
                <SelectItem value="proposal">Proposal</SelectItem><SelectItem value="negotiation">Negotiation</SelectItem><SelectItem value="closing">Closing</SelectItem>
                <SelectItem value="won">Won</SelectItem><SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
          ) : filteredDeals.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader><TableRow><TableHead>Business Name</TableHead><TableHead>Industry</TableHead><TableHead>Value</TableHead><TableHead>Status</TableHead><TableHead>Contact</TableHead><TableHead>Expected Close</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filteredDeals.map((deal) => (
                    <TableRow key={deal.id}>
                      <TableCell className="font-medium">{deal.businessName}</TableCell>
                      <TableCell>{deal.industry}</TableCell>
                      <TableCell>${deal.dealValue.toLocaleString()}</TableCell>
                      <TableCell><Badge className={getStatusColor(deal.status)}>{deal.status.charAt(0).toUpperCase() + deal.status.slice(1)}</Badge></TableCell>
                      <TableCell><div className="text-sm"><div>{deal.contactPerson}</div><div className="text-muted-foreground">{deal.contactEmail}</div></div></TableCell>
                      <TableCell>{deal.expectedCloseDate?.toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mb-4" />
              <p>No deals yet. Create your <span className="text-primary cursor-pointer" onClick={() => setIsDialogOpen(true)}>first deal</span> to get started.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
